import React, { useMemo } from "react";
import Select from "react-select";
import { useFormikContext, ErrorMessage } from "formik";
import { useGetCommunicationTemplatesQuery } from "../services/redux/api/components";

const TemplateDropdown = ({
  name = "TemplateID",
  defaultTemplateName = null,
  communicationCode = null,
  isActive = true,
  onChange,
  style = {},
  disabled = false,
}) => {
  const { setFieldValue, values } = useFormikContext();

  const { data: response, isLoading, isFetching } =
    useGetCommunicationTemplatesQuery({
      communication_code: communicationCode,
      is_active: isActive,
      pageSize: 1000,
    });

  const rowData = useMemo(() => {
    const rows = response?.data;
    if (!rows || !Array.isArray(rows)) return [];

    const preDefinedOptions = [];
    const customOptions = [];

    rows.forEach((item) => {
      const option = {
        ...item,
        value: item.id ?? item.communication_code ?? "",
        label: item.communication_name ?? item.communication_code ?? "Template",
      };

      if (item.template_type?.trim() === 'META') {
        preDefinedOptions.push(option);
      } else {
        customOptions.push(option);
      }
    });

    const groupedOptions = [];
    if (preDefinedOptions.length > 0) {
      groupedOptions.push({ label: "Pre Defined Template", options: preDefinedOptions });
    }
    if (customOptions.length > 0) {
      groupedOptions.push({ label: "Custom Templates", options: customOptions });
    }

    return groupedOptions;
  }, [response]);

  const selectedValue = useMemo(() => {
    const flatOptions = rowData.flatMap(group => group.options || []);
    let found = flatOptions.find((opt) => String(opt.value) === String(values[name]));
    
    // Fallback: If not found by ID, but we have a default name, find by name
    if (!found && defaultTemplateName) {
      found = flatOptions.find(opt => 
        (opt.communication_name && opt.communication_name.toLowerCase() === defaultTemplateName.toLowerCase()) || 
        (opt.label && opt.label.toLowerCase() === defaultTemplateName.toLowerCase())
      );
    }
    
    return found || null;
  }, [rowData, values, name, defaultTemplateName]);

  React.useEffect(() => {
    if (selectedValue) {
      if (values[name] !== selectedValue.value) {
        setFieldValue(name, selectedValue.value);
      }
      if (onChange) {
        onChange(selectedValue.value, selectedValue);
      }
    }
  }, [selectedValue]); // intentionally not including onChange to avoid loops if it's not memoized

  const handleChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFieldValue(name, value);
    if (onChange) onChange(value, selectedOption);
  };

  const { height, width, ...otherStyles } = style;
  const controlHeight = height || "45px";

  const inputStyle = {
    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
    minHeight: controlHeight,
    borderRadius: "10px",
    border: "1px solid #D1D5DB",
    fontSize: "14px",
    backgroundColor: "#fff",
    ...otherStyles,
  };

  const customStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        ...inputStyle,
        width: width || base.width,
        border: state.isFocused
          ? "1px solid #EB2B21"
          : disabled
            ? "1px solid #ccc"
            : inputStyle.border,
        backgroundColor: disabled ? "#e9ecef" : inputStyle.backgroundColor,
        boxShadow: state.isFocused
          ? "2px 2px 5px rgba(0, 0, 0, 0.25)"
          : inputStyle.boxShadow,
        cursor: disabled ? "not-allowed" : "pointer",
        outline: "none",
        "&:hover": {
          borderColor: state.isFocused
            ? "#EB2B21"
            : disabled
              ? "#ccc"
              : "#D1D5DB",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        padding: "2px 8px",
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: "14px",
      }),
      singleValue: (base) => ({
        ...base,
        fontSize: "14px",
        color: disabled ? "#6c757d" : "#212529",
      }),
      placeholder: (base) => ({
        ...base,
        fontSize: "14px",
        color: "#9CA3AF",
      }),
      dropdownIndicator: (base) => ({
        ...base,
        display: disabled ? "none" : "flex",
      }),
      indicatorSeparator: () => ({
        display: disabled ? "none" : "block",
      }),
      clearIndicator: (base) => ({
        ...base,
        display: disabled ? "none" : "flex",
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: "10px",
        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.15)",
      }),
    }),
    [disabled, width, inputStyle]
  );

  return (
    <div className="px-0">
      <label className="form-label">Template</label>
      <Select
        name={name}
        options={rowData}
        isLoading={isLoading || isFetching}
        value={selectedValue}
        onChange={handleChange}
        placeholder={
          isLoading || isFetching ? "Loading templates..." : "Select Template"
        }
        isClearable
        isSearchable
        classNamePrefix="react-select"
        styles={customStyles}
      />
      <ErrorMessage
        name={name}
        component="div"
        className="text-red-600 text-sm mt-1"
      />
    </div>
  );
};

export default TemplateDropdown;
